import { ParentFieldProvider, IParentFieldInfo } from "WorkItemTracking/Scripts/Controls/Query/ParentFieldProvider";
import { WorkItemManager } from "WorkItemTracking/Scripts/OM/WorkItemManager";
import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import { ProjectCollection } from "Presentation/Scripts/TFS/TFS.OM.Common";
import { WorkItemStore } from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import { CoreFieldRefNames } from "Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants";

/**
 * Interface implemented by data sources for boards that may display the parent field.
 * Allows the BoardParentDataProvider to access parent data that has already
 * been paged in/updated on the board.
 */
export interface IBoardFieldValueProvider {
    hasWorkItem(workItemId: number): boolean;
    getFieldValue(workItemId: number, fieldName: string): any;
}

/**
 * Provides data for the 'parent' field.  Since the 'parent' field is just an integer work item id,
 * this provides data about that work item by either getting it from the work item manager, the
 * paged data provided by the board, or getting it from the Artifact resolver.
 * 
 * There can only be one instance of this class instantiated at a time since some of the data may
 * come from the board itself.
 */
export class BoardParentDataProvider {

    private static _dataProvider: BoardParentDataProvider;
    private _provider: ParentFieldProvider;
    private _workItemSource: IBoardFieldValueProvider;

    public static getInstance() {
        return BoardParentDataProvider._dataProvider;
    }

    public static clear() {
        if (BoardParentDataProvider._dataProvider) {
            BoardParentDataProvider._dataProvider._provider = null;
            BoardParentDataProvider._dataProvider._workItemSource = null;
            BoardParentDataProvider._dataProvider = null;
        }
    }

    public static create(workItemSource: IBoardFieldValueProvider) {
        BoardParentDataProvider._dataProvider = new BoardParentDataProvider(workItemSource);
    }

    constructor(workItemSource: IBoardFieldValueProvider) {
        this._provider = null;
        this._workItemSource = workItemSource;
    }

    public get(workItemId: number): IPromise<IParentFieldInfo> {

        return new Promise<IParentFieldInfo>((resolve, reject) => {

            const parentInfo = this.getCached(workItemId);

            if (parentInfo) {
                resolve(parentInfo);
            }
            else {
                this._provider.load([workItemId]).then((parentInfos) => {
                    resolve(parentInfos[0]);
                });
            }
        });
    }

    public getCached(workItemId: number): IParentFieldInfo | undefined {
        const manager = WorkItemManager.get(ProjectCollection.getDefaultConnection().getService<WorkItemStore>(WorkItemStore));
        const wi = manager.getWorkItem(workItemId);

        if (wi) {
            return {
                id: workItemId,
                type: wi.workItemType.name,
                project: wi.project.name,
                title: wi.getTitle()
            };
        }

        // Try the work item source data
        if (this._workItemSource.hasWorkItem(workItemId)) {

            return {
                id: workItemId,
                type: this._workItemSource.getFieldValue(workItemId, CoreFieldRefNames.WorkItemType),
                project: this._workItemSource.getFieldValue(workItemId, CoreFieldRefNames.TeamProject) || TfsContext.getDefault().navigation.project,
                title: this._workItemSource.getFieldValue(workItemId, CoreFieldRefNames.Title)
            };
        }

        // Finally, get the data from the server if all else fails
        if (!this._provider) {
            var tfsContext = TfsContext.getDefault();
            this._provider = new ParentFieldProvider(tfsContext, tfsContext.navigation.project);
        }

        return this._provider.get(workItemId);
    }

    public invalidate(id: number): void {
        if (this._provider) {
            this._provider.invalidate(id);
        }
    }
}